"use client";

import { useMemo } from "react";
import { useTheme } from "@/contexts/theme-context";
import CodeMirror from "@uiw/react-codemirror";
import { EditorView } from "@codemirror/view";
import {
  HighlightStyle,
  StreamLanguage,
  syntaxHighlighting,
} from "@codemirror/language";
import { highlightSelectionMatches } from "@codemirror/search";
import { tags as t } from "@lezer/highlight";
import { csharp } from "@codemirror/legacy-modes/mode/clike";
import { json } from "@codemirror/legacy-modes/mode/javascript";
import type { EditorLanguage } from "@/lib/custom-indicators/types";

const csharpEditorLanguage = StreamLanguage.define(csharp);
const jsonLanguage = StreamLanguage.define(json);

const MONO_FONT =
  'ui-monospace, "SF Mono", SFMono-Regular, "JetBrains Mono", "Fira Code", Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace';

function makeChrome(dark: boolean) {
  const selection = dark
    ? "hsl(217 91% 60% / 0.32)"
    : "hsl(217 91% 55% / 0.22)";
  const selectionMatch = dark
    ? "hsl(45 90% 55% / 0.22)"
    : "hsl(45 90% 45% / 0.22)";
  const scrollbarThumb = dark ? "hsl(0 0% 28%)" : "hsl(0 0% 75%)";
  const scrollbarThumbHover = dark ? "hsl(0 0% 38%)" : "hsl(0 0% 65%)";

  return EditorView.theme(
    {
      "&": {
        backgroundColor: "hsl(var(--background))",
        color: "hsl(var(--foreground))",
        height: "100%",
      },
      ".cm-editor": {
        backgroundColor: "hsl(var(--background))",
      },
      "&.cm-editor.cm-focused": {
        outline: "none",
      },
      ".cm-scroller": {
        backgroundColor: "hsl(var(--background))",
        fontFamily: MONO_FONT,
        lineHeight: "1.6",
      },
      ".cm-content": {
        caretColor: "hsl(var(--foreground))",
        backgroundColor: "transparent",
        padding: "8px 0",
      },
      ".cm-line": {
        padding: "0 10px",
        backgroundColor: "transparent",
      },
      ".cm-selectionLayer": {
        zIndex: "-1",
      },
      ".cm-cursor, .cm-dropCursor": {
        borderLeftWidth: "2px",
      },
      "&.cm-focused .cm-cursor": {
        borderLeftColor: "hsl(var(--foreground))",
      },
      "&.cm-focused .cm-selectionBackground, .cm-selectionBackground, .cm-content ::selection":
        {
          backgroundColor: `${selection} !important`,
        },
      ".cm-selectionLayer .cm-selectionBackground": {
        borderRadius: "2px",
      },
      ".cm-selectionMatch": {
        backgroundColor: selectionMatch,
        borderRadius: "2px",
        outline: `1px solid ${
          dark ? "hsl(45 90% 55% / 0.4)" : "hsl(45 90% 45% / 0.4)"
        }`,
      },
      ".cm-gutters": {
        backgroundColor: "hsl(var(--background))",
        color: "hsl(var(--muted-foreground))",
        border: "none",
        userSelect: "none",
      },
      ".cm-lineNumbers .cm-gutterElement": {
        padding: "0 10px 0 12px",
        minWidth: "2.5ch",
      },
      ".cm-activeLine": {
        backgroundColor: "hsl(var(--muted) / 0.45)",
      },
      ".cm-activeLineGutter": {
        backgroundColor: "hsl(var(--muted) / 0.45)",
        color: "hsl(var(--foreground))",
        boxShadow: "inset 2px 0 0 0 hsl(var(--ring))",
      },
      ".cm-foldGutter .cm-gutterElement": {
        cursor: "pointer",
        opacity: "0.5",
        transition: "opacity 120ms ease",
      },
      ".cm-foldGutter .cm-gutterElement:hover": {
        opacity: "1",
        color: "hsl(var(--foreground))",
      },
      ".cm-foldPlaceholder": {
        backgroundColor: "hsl(var(--muted))",
        color: "hsl(var(--muted-foreground))",
        border: "1px solid hsl(var(--border))",
        borderRadius: "4px",
        padding: "0 6px",
        margin: "0 2px",
      },
      "&.cm-focused .cm-matchingBracket": {
        backgroundColor: "hsl(160 60% 45% / 0.28)",
        outline: "1px solid hsl(160 60% 45% / 0.5)",
        borderRadius: "2px",
      },
      "&.cm-focused .cm-nonmatchingBracket": {
        backgroundColor: "hsl(var(--destructive) / 0.25)",
        outline: "1px solid hsl(var(--destructive) / 0.5)",
        borderRadius: "2px",
      },
      ".cm-panels": {
        backgroundColor: "hsl(var(--card))",
        color: "hsl(var(--foreground))",
        borderTop: "1px solid hsl(var(--border))",
        borderBottom: "1px solid hsl(var(--border))",
      },
      ".cm-panel.cm-search": {
        padding: "8px 10px",
        fontFamily: "inherit",
        fontSize: "12px",
      },
      ".cm-panel.cm-search input, .cm-panel.cm-search button, .cm-panel.cm-search label":
        {
          fontFamily: "inherit",
          fontSize: "12px",
        },
      ".cm-textfield": {
        backgroundColor: "hsl(var(--background))",
        color: "hsl(var(--foreground))",
        border: "1px solid hsl(var(--border))",
        borderRadius: "6px",
        padding: "3px 8px",
        outline: "none",
      },
      ".cm-textfield:focus": {
        borderColor: "hsl(var(--ring))",
        boxShadow: "0 0 0 1px hsl(var(--ring))",
      },
      ".cm-button": {
        backgroundColor: "hsl(var(--secondary))",
        color: "hsl(var(--secondary-foreground))",
        border: "1px solid hsl(var(--border))",
        borderRadius: "6px",
        padding: "3px 10px",
        backgroundImage: "none",
        cursor: "pointer",
        transition: "background-color 120ms ease",
      },
      ".cm-button:hover": {
        backgroundColor: "hsl(var(--muted))",
      },
      ".cm-panel.cm-search label": {
        color: "hsl(var(--muted-foreground))",
      },
      ".cm-panel button[name='close']": {
        color: "hsl(var(--muted-foreground))",
        cursor: "pointer",
      },
      ".cm-panel button[name='close']:hover": {
        color: "hsl(var(--foreground))",
      },
      ".cm-tooltip": {
        backgroundColor: "hsl(var(--popover))",
        color: "hsl(var(--popover-foreground))",
        border: "1px solid hsl(var(--border))",
        borderRadius: "8px",
        boxShadow:
          "0 4px 12px hsl(0 0% 0% / 0.25), 0 2px 4px hsl(0 0% 0% / 0.15)",
      },
      ".cm-tooltip.cm-tooltip-autocomplete > ul > li[aria-selected]": {
        backgroundColor: "hsl(var(--accent))",
        color: "hsl(var(--accent-foreground))",
      },
      ".cm-scroller::-webkit-scrollbar": {
        width: "10px",
        height: "10px",
      },
      ".cm-scroller::-webkit-scrollbar-track": {
        backgroundColor: "transparent",
      },
      ".cm-scroller::-webkit-scrollbar-thumb": {
        backgroundColor: scrollbarThumb,
        borderRadius: "8px",
        border: "2px solid hsl(var(--background))",
      },
      ".cm-scroller::-webkit-scrollbar-thumb:hover": {
        backgroundColor: scrollbarThumbHover,
      },
    },
    { dark }
  );
}

const vybeDarkHighlight = HighlightStyle.define([
  { tag: [t.comment, t.lineComment, t.blockComment], color: "hsl(0 0% 45%)", fontStyle: "italic" },
  { tag: [t.keyword, t.modifier, t.controlKeyword, t.operatorKeyword], color: "hsl(280 65% 70%)" },
  { tag: [t.string, t.special(t.string)], color: "hsl(160 60% 55%)" },
  { tag: [t.number, t.bool, t.null], color: "hsl(30 80% 62%)" },
  { tag: [t.tagName, t.typeName, t.className, t.namespace], color: "hsl(173 58% 55%)" },
  { tag: [t.function(t.variableName), t.function(t.propertyName)], color: "hsl(217 91% 65%)" },
  { tag: [t.propertyName, t.attributeName], color: "hsl(217 91% 70%)" },
  { tag: [t.variableName, t.definition(t.variableName)], color: "hsl(0 0% 90%)" },
  { tag: [t.operator, t.punctuation, t.bracket], color: "hsl(0 0% 63.9%)" },
  { tag: [t.meta, t.annotation], color: "hsl(340 75% 65%)" },
  { tag: t.invalid, color: "hsl(0 84% 60%)" },
]);

const vybeLightHighlight = HighlightStyle.define([
  { tag: [t.comment, t.lineComment, t.blockComment], color: "hsl(0 0% 45%)", fontStyle: "italic" },
  { tag: [t.keyword, t.modifier, t.controlKeyword, t.operatorKeyword], color: "hsl(280 65% 45%)" },
  { tag: [t.string, t.special(t.string)], color: "hsl(160 60% 32%)" },
  { tag: [t.number, t.bool, t.null], color: "hsl(30 80% 42%)" },
  { tag: [t.tagName, t.typeName, t.className, t.namespace], color: "hsl(173 58% 32%)" },
  { tag: [t.function(t.variableName), t.function(t.propertyName)], color: "hsl(217 91% 50%)" },
  { tag: [t.propertyName, t.attributeName], color: "hsl(217 91% 45%)" },
  { tag: [t.variableName, t.definition(t.variableName)], color: "hsl(0 0% 15%)" },
  { tag: [t.operator, t.punctuation, t.bracket], color: "hsl(0 0% 40%)" },
  { tag: [t.meta, t.annotation], color: "hsl(340 75% 45%)" },
  { tag: t.invalid, color: "hsl(0 84% 50%)" },
]);

const chromeDark = makeChrome(true);
const chromeLight = makeChrome(false);

function languageExtension(language: EditorLanguage) {
  switch (language) {
    case "json":
      return jsonLanguage;
    case "csharp":
      return csharpEditorLanguage;
    case "markdown":
    case "text":
    default:
      return null;
  }
}

interface CodeEditorProps {
  value: string;
  onChange: (value: string) => void;
  language?: EditorLanguage;
  className?: string;
  readOnly?: boolean;
}

export default function CodeEditor({
  value,
  onChange,
  language = "csharp",
  className,
  readOnly = false,
}: CodeEditorProps) {
  const { actualTheme } = useTheme();
  const isDark = actualTheme === "dark";

  const extensions = useMemo(() => {
    const langExt = languageExtension(language);
    const exts = [
      isDark ? chromeDark : chromeLight,
      syntaxHighlighting(isDark ? vybeDarkHighlight : vybeLightHighlight),
      highlightSelectionMatches(),
      EditorView.lineWrapping,
    ];
    if (langExt) {
      exts.unshift(langExt);
    }
    return exts;
  }, [isDark, language]);

  return (
    <CodeMirror
      value={value}
      height="100%"
      className={
        className ??
        "h-full text-sm [&_.cm-editor]:h-full [&_.cm-scroller]:min-h-0"
      }
      theme="none"
      extensions={extensions}
      onChange={onChange}
      editable={!readOnly}
      basicSetup={{
        lineNumbers: true,
        foldGutter: true,
        highlightActiveLine: true,
        highlightActiveLineGutter: true,
        bracketMatching: true,
        closeBrackets: true,
        highlightSelectionMatches: false,
        searchKeymap: true,
        autocompletion: false,
      }}
    />
  );
}
